import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { UtilsModule } from '@vsolv/ui-kit/utils';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { BadgeModule } from '@vsolv/vectors-ui/badge';
import { ButtonModule } from '@vsolv/vectors-ui/button';
import { DialogComponent, DialogConfig, DialogHeaderConfig, DialogModule } from '@vsolv/vectors-ui/dialog';
import { FormsModule } from '@vsolv/vectors-ui/forms';
import { IconsModule } from '@vsolv/vectors-ui/icons';
import { InputModule } from '@vsolv/vectors-ui/input';
import { ThemingModule } from '@vsolv/vectors-ui/theming';
import { TooltipModule } from '@vsolv/vectors-ui/tooltip';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ItemStatusBadgeComponent } from '../../components/item-status-badge';
import { StorageSecurityService, StorageService } from '../../services';

@Component({
  standalone: true,
  selector: 'vs-edit-document-dialog',
  templateUrl: './edit-document.dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    InputModule,
    BadgeModule,
    UtilsModule,
    IconsModule,
    CommonModule,
    DialogModule,
    ButtonModule,
    ThemingModule,
    TooltipModule,
    ReactiveFormsModule,
    ItemStatusBadgeComponent,
  ],
})
export class EditDocumentDialog implements OnDestroy {
  private toastSvc = inject(ToastService);
  private storageSvc = inject(StorageService);
  private securitySvc = inject(StorageSecurityService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  private onDestroy$ = new Subject<void>();

  canDelete$ = this.securitySvc.canDelete$;
  canUpdate$ = this.securitySvc.canUpdate$;

  private _closedSub?: Subscription;
  private _openedSub?: Subscription;

  private _instance?: DialogComponent;
  @ViewChild(DialogComponent) private set instance(instance: DialogComponent | undefined) {
    this._instance = instance;

    if (this._closedSub) this._closedSub.unsubscribe();
    this._closedSub = this.instance?.closed
      .pipe(takeUntil(this.onDestroy$))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe(data => this.closed.emit(data as any));

    if (this._openedSub) this._openedSub.unsubscribe();
    this._openedSub = this.instance?.opened.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.opened.emit());
  }
  get instance() {
    return this._instance;
  }

  protected dialogConfig: DialogConfig = { dialogContainerClass: '!max-w-[80vw]' };
  protected dialogHeaderConfig: DialogHeaderConfig = {
    mode: 'simple',
    showDivider: true,
    iconTheme: 'primary',
    title: 'Edit document',
    icon: 'upload-cloud-01',
    subtitle: 'Edit the details of this document',
  };

  protected get filename() {
    return this.document?.externalId?.replace(/.*\//, '');
  }

  protected get extension() {
    return (
      this.document?.extension ??
      StorageItem.getFileExtension(this.document?.originalFilename ?? this.document?.externalId) ??
      this.document?.mimeType
    );
  }

  protected get pristine() {
    const formValue = {
      title: this.form.value.title,
      description: this.form.value.description || null,
      visibility: this.form.value.visibility || 'private',
    };

    const itemValue = {
      title: this.document?.title,
      description: this.document?.description || null,
      visibility: this.document?.privateFile ? 'private' : 'public',
    };

    return this.form.pristine || JSON.stringify(formValue) === JSON.stringify(itemValue);
  }

  protected form = new FormGroup({
    visibility: new FormControl<'private' | 'public'>('private', [Validators.required]),
    title: new FormControl<string | null>(null, [Validators.required, Validators.minLength(1)]),
    description: new FormControl<string | null>(null),
  });

  protected saving = false;
  protected deleting = false;
  protected confirmDelete = false;

  @Output() closed = new EventEmitter<{ deleted?: true; updated?: true } | null>();
  @Output() opened = new EventEmitter();

  @Input() document?: StorageItem.Model | null;

  @Input() hideStatusBadge?: boolean | null;
  @Input() hideVisibilityInput?: boolean | null;

  @Input() customEndpoint?: string | null;

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  open() {
    this.reset();
    this.instance?.open();
    this.changeDetectorRef.markForCheck();
  }

  close(data: { deleted?: true; updated?: true } | null = null) {
    this.instance?.close(data);
    this.changeDetectorRef.markForCheck();
  }

  reset() {
    this.saving = false;
    this.deleting = false;
    this.confirmDelete = false;

    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: false };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: false };

    this.form.reset({
      title: this.document?.title ?? null,
      description: this.document?.description ?? null,
      visibility: this.document?.privateFile ? 'private' : 'public',
    });
  }

  protected async save() {
    const id = this.document?.id;
    if (!id) return;

    const value = this.form.value;
    if (!value.title) return;

    this.form.disable();

    this.saving = true;
    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: true };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: true };

    try {
      await new Promise(res => setTimeout(res, 1000));
      await this.storageSvc.update(
        id,
        {
          title: value.title,
          description: value.description || null,
          privateFile: value.visibility === 'private',
        },
        this.customEndpoint
      );

      this.close({ updated: true });
      this.success(value.title);
    } catch (err) {
      console.error(err);
      this.failure(value.title);
    }

    this.saving = false;
    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: false };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: false };

    this.form.enable();

    this.changeDetectorRef.markForCheck();
  }

  protected async deleteDocument() {
    const title = this.document?.title,
      id = this.document?.id;
    if (!title || !id) return;

    this.form.disable();

    this.deleting = true;
    this.confirmDelete = false;
    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: true };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: true };

    try {
      await new Promise(res => setTimeout(res, 1000));
      await this.storageSvc.delete(id, this.customEndpoint);

      this.close({ deleted: true });
      this.success(title, false);
    } catch (err) {
      console.error(err);
      this.failure(title, false);
    }

    this.deleting = false;
    this.dialogConfig = { ...this.dialogConfig, disableBackdropDismiss: false };
    this.dialogHeaderConfig = { ...this.dialogHeaderConfig, hideCloseButton: false };

    this.form.enable();

    this.changeDetectorRef.markForCheck();
  }

  private success(title: string, updating = true) {
    this.toastSvc.show({
      type: 'success',
      title: `Document ${updating ? 'updated' : 'deleted'}`,
      text: `Document '${title}' has been successfully ${updating ? 'updated' : 'deleted'}.`,
    });
  }

  private failure(title: string, updating = true) {
    this.toastSvc.show({
      type: 'error',
      title: `${updating ? 'Update' : 'Deletion'} failed`,
      text: `Failed to ${updating ? 'update' : 'delete'} document '${title}'. Please try again.`,
    });
  }
}
